import { Box, CircularProgress, circularProgressClasses } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ScaleLoader } from "react-spinners";
import Layout from "../../Layout/Layout";
import { Helmet } from "react-helmet";

const CategoryPage = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const [productList, setProductList] = useState([]);
  const [categoryBanner, setCategoryBanner] = useState(null);
  const [apiLoader, setApiLoader] = useState(true);
  const [spinner, setSpinner] = useState(true);

  const id = location?.state?.id || null;
  const name = location?.state?.name || searchParams.get("name");

  useEffect(() => {
    fetchCategoryProducts();
    window.scrollTo(0, 0);
  }, [id, params.categoryName]);

  const fetchCategoryProducts = async () => {
    try {
      setApiLoader(true);
      setSpinner(true);

      const url = id
        ? `${process.env.REACT_APP_BASE_URL}getbyCategory/${id}`
        : `${process.env.REACT_APP_BASE_URL}getprobycategerySlug/${params.categoryName}`;

      const response = await fetch(url);
      console.log("api response : ", response);

      const result = await response.json();

      console.log("api result : ", result);

      if (result?.status === 200) {
        setProductList(result?.data || []);
        setCategoryBanner(result?.cat_image || null);
      } else {
        setProductList([]);
      }
    } catch (error) {
      console.error("API Error:", error);
      setProductList([]);
    } finally {
      setApiLoader(false);
      setSpinner(false);
    }
  };

  const FacebookCircularProgress = () => (
    <Box sx={{ position: "relative" }}>
      <CircularProgress
        variant="determinate"
        size={40}
        thickness={4}
        value={100}
        sx={{
          color: (theme) =>
            theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
        }}
      />
      <CircularProgress
        variant="indeterminate"
        disableShrink
        size={40}
        thickness={4}
        sx={{
          color: "#008000",
          animationDuration: "550ms",
          position: "absolute",
          left: 0,
          [`& .${circularProgressClasses.circle}`]: {
            strokeLinecap: "round",
          },
        }}
      />
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>{`AZAMDEAL - ${name || "Category"}`}</title>
        <meta name="description" content={`AZAMDEAL - ${name || "Category"}`} />
        {categoryBanner && (
          <meta
            property="og:image"
            content={`${process.env.REACT_APP_MEDIA_BASE_URL}${categoryBanner}`}
          />
        )}
      </Helmet>

      <Layout>
        {apiLoader ? (
          <div className="h-screen flex justify-center items-center bg-white">
            <FacebookCircularProgress />
          </div>
        ) : (
          <div className="p-1 md:p-5 lg:p-10">
            {/* ✅ CATEGORY BANNER (Always visible if exists) */}
            {categoryBanner && (
              <div>
                <img
                  src={process.env.REACT_APP_MEDIA_BASE_URL + categoryBanner}
                  alt={name || "Category Banner"}
                  className="w-full h-40 sm:h-60 lg:h-80 object-cover rounded-xl md:rounded-tl-3xl md:rounded-br-3xl lg:rounded-tl-full lg:rounded-br-full"
                />
              </div>
            )}

            <div className="pt-8 lg:pt-10">
              <h3 className="text-lg lg:text-xl mb-3 font-semibold font-serif">
                {name}
              </h3>
              <hr />

              {/* 🔄 PRODUCT LOADER */}
              {spinner && (
                <div className="flex justify-center my-20">
                  <ScaleLoader color="#36d7b7" />
                </div>
              )}

              {/* ❌ NO PRODUCTS */}
              {!spinner && productList.length === 0 && (
                <div className="text-center text-gray-500 py-20">
                  <h2 className="text-lg font-semibold">
                    No products found in this category
                  </h2>
                </div>
              )}

              {/* ✅ PRODUCT GRID */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3 mt-6">
                {productList.map((el, index) => {
                  const productSlug = el.product_name
                    .toLowerCase()
                    .replace(/\s+/g, "-")
                    .replace(/[^a-z0-9-]/g, "");

                  return (
                    <div
                      key={index}
                      className="group bg-white shadow-sm rounded-md overflow-hidden cursor-pointer"
                      onClick={() =>
                        navigate(`/Product/${productSlug}`, {
                          state: { id: el.id, name: el.product_name },
                        })
                      }
                    >
                      <img
                        src={
                          process.env.REACT_APP_MEDIA_BASE_URL +
                          el.product_base_image
                        }
                        alt={el.product_name}
                        className="h-52 w-full object-scale-down group-hover:scale-105 transition"
                      />

                      <div className="p-3">
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {el.product_name}
                        </p>

                        <div className="flex items-center gap-2 mt-2">
                          <strike className="text-xs text-gray-400">
                            ₹{el.var_mrp || el.maximum_retail_price}
                          </strike>
                          <span className="text-green-600 font-bold">
                            ₹{el.var_price || el.discounted_price}
                          </span>
                        </div>

                        {el.var_mrp && el.var_price && (
                          <p className="text-xs text-gray-400">
                            (
                            {Math.floor(
                              100 - (el.var_price / el.var_mrp) * 100,
                            )}
                            % off)
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Layout>
    </>
  );
};

export default CategoryPage;
